import React, { forwardRef } from "react";
import { ButtonProps } from "./Button.types";
import { useTheme } from "../../../theme/ThemeProvider";
import LoadingSpinner from "../LoadingSpinner";

/**
 * Button Component
 *
 * A customizable button component that uses the theme system
 * for styling and provides various variants and sizes.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "md",
      isLoading = false,
      className = "",
      fullWidth = false,
      startIcon,
      endIcon,
      disabled,
      type = "button",
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    // Base styles for all buttons
    const baseStyles =
      "inline-flex items-center justify-center font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2";

    // Size-based styles
    const sizeStyles = {
      sm: "px-3 py-1.5 text-sm rounded-md",
      md: "px-4 py-2 text-base rounded-md",
      lg: "px-6 py-3 text-lg rounded-lg",
    };

    // Check if we're in a booking page context by looking for CSS variable
    const isBookingContext = typeof window !== 'undefined' &&
      getComputedStyle(document.documentElement).getPropertyValue('--brand-color').trim() !== '';

    // Variant-based styles
    const variantStyles = {
      // Primary button uses CSS variables on booking pages for dynamic meeting colors
      primary: isBookingContext
        ? `text-white focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`
        : `bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 focus:ring-primary-500 disabled:bg-primary-300 disabled:text-secondary-100`,

      secondary: `bg-[${theme.buttons.secondary.background}] text-[${theme.buttons.secondary.text}] border ${theme.buttons.secondary.border} hover:bg-[${theme.buttons.secondary.hoverBackground}] active:bg-[${theme.buttons.secondary.activeBackground}] focus:ring-[${theme.colors.primary}] disabled:bg-[${theme.buttons.secondary.disabledBackground}] disabled:text-[${theme.buttons.secondary.disabledText}]`,

      danger: `bg-[${theme.buttons.danger.background}] text-[${theme.buttons.danger.text}] hover:bg-[${theme.buttons.danger.hoverBackground}] active:bg-[${theme.buttons.danger.activeBackground}] focus:ring-[${theme.colors.danger}]`,

      text: `bg-transparent text-[${theme.colors.primary}] hover:bg-[${theme.colors.light}] focus:ring-[${theme.colors.primary}]`,

      link: `bg-transparent text-[${theme.colors.primary}] hover:underline focus:ring-0 p-0`,
    };

    // Inline styles for primary button in booking context (use CSS variables)
    const primaryBookingStyles: React.CSSProperties | undefined =
      variant === 'primary' && isBookingContext
        ? {
            backgroundColor: 'var(--brand-color, #2E7AA0)',
            borderColor: 'var(--brand-color, #2E7AA0)',
          }
        : undefined;

    // Determine spinner color based on button variant
    const getSpinnerColor = () => {
      if (variant === 'primary' && isBookingContext) return 'brand';
      if (variant === 'primary' || variant === 'danger') return 'white';
      return 'primary';
    };

    // Combine all styles
    const combinedClassNames = [
      baseStyles,
      sizeStyles[size],
      variantStyles[variant],
      fullWidth ? "w-full" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // Combine styles
    const inlineStyle = primaryBookingStyles ? { ...primaryBookingStyles } : {};

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        className={combinedClassNames}
        style={inlineStyle}
        onMouseEnter={(e) => {
          if (variant === 'primary' && isBookingContext && !disabled && !isLoading) {
            e.currentTarget.style.backgroundColor = 'var(--brand-color-hover, #276891)';
          }
        }}
        onMouseLeave={(e) => {
          if (variant === 'primary' && isBookingContext && !disabled && !isLoading) {
            e.currentTarget.style.backgroundColor = 'var(--brand-color, #2E7AA0)';
          }
        }}
        onMouseDown={(e) => {
          if (variant === 'primary' && isBookingContext && !disabled && !isLoading) {
            e.currentTarget.style.backgroundColor = 'var(--brand-color-active, #1F5275)';
          }
        }}
        onMouseUp={(e) => {
          if (variant === 'primary' && isBookingContext && !disabled && !isLoading) {
            e.currentTarget.style.backgroundColor = 'var(--brand-color-hover, #276891)';
          }
        }}
        {...rest}
      >
        {isLoading && (
          <LoadingSpinner
            size="sm"
            color={getSpinnerColor()}
            className="-ml-1 mr-2"
          />
        )}
        {!isLoading && startIcon && <span className="mr-2">{startIcon}</span>}
        {children}
        {!isLoading && endIcon && <span className="ml-2">{endIcon}</span>}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
